import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { createDummyApi } from '@/dummyData';
import { useBackend } from '@/hooks/useBackend';
import { useInternalApiFilterParams } from '@/hooks/useInternalApiFilterParams';
import useSWRExpect404 from '@/hooks/useSWRExpect404';
import { InternalApiResponse } from '@/openapi/schema';
import { PaginatedResults } from '@/pages/explorer/components/PaginatedResults';

import ApiCard from '../../components/ApiCard';

const DUMMY_APIS = new Array(5).fill(null).map((_, id) => createDummyApi(id));

export function PaginatedInternalApis() {
  const [searchParams] = useSearchParams();
  const filterParams = useInternalApiFilterParams();
  const page = Math.max(Number(searchParams.get('page')) || 0, 0);

  const swrExpect404 = useSWRExpect404();

  const {
    data: apiResponse,
    isLoading,
    error,
    mutate,
  } = useBackend(
    '/apis/internal',
    { params: { query: { page, ...filterParams } } },
    { keepPreviousData: true, ...swrExpect404 },
  );

  const onApiFavouriteChange = useCallback(
    (apiId: string, isFavorite: boolean) => {
      void mutate(
        (prev) => {
          if (!prev) return;
          const updated = structuredClone(prev);
          const api = updated.content.find((a) => a.id === apiId);
          if (api) {
            api.isFavorite = isFavorite;
          }
          return updated;
        },
        { revalidate: false },
      );
    },
    [mutate],
  );

  return (
    <PaginatedResults
      data={apiResponse}
      emptyMessage="No APIs match the selected filters."
      error={error}
      errorMessage="Failed to fetch APIs."
      isLoading={isLoading}
      itemLabel={{ singular: 'API', plural: 'APIs' }}
      renderItems={(apis: InternalApiResponse[]) => (
        <div>
          {apis.map((api) => (
            <ApiCard api={api} key={api.id} onApiFavouriteChange={onApiFavouriteChange} />
          ))}
        </div>
      )}
      renderSkeleton={() => (
        <div>
          {DUMMY_APIS.map((api) => (
            <ApiCard api={api} key={api.id} onApiFavouriteChange={() => {}} />
          ))}
        </div>
      )}
    />
  );
}
